export type AutomationBoardItem = {
  id: string;
  title: string;
  automationStatus: string;
  workflowStatus: string;
  department: string;
  priority: string;
  brands: string[];
  summary: string;
  problem: string;
  overview: string;
  url: string;
  createdAt: string;
  updatedAt: string;
};

export type AutomationBoardLane = {
  status: string;
  count: number;
  items: AutomationBoardItem[];
};

export type AutomationBoardResponse = {
  live: boolean;
  source: string;
  databaseTitle: string;
  updatedAt: string;
  countsByAutomationStatus: Record<string, number>;
  lanes: AutomationBoardLane[];
  items: AutomationBoardItem[];
};

export type AutomationRequestDraft = {
  name: string;
  brand: string;
  department: string;
  priority: string;
  task: string;
  currentProcess: string;
  desiredOutcome: string;
};

type AutomationRequestCreateResponse = {
  ok: boolean;
  message: string;
  item: AutomationBoardItem;
};

const AUTOMATION_ENDPOINT = '/analytics/brain-dashboard/v1/automation-requests';

const ensureApiBaseUrl = (apiBaseUrl: string): string => {
  const trimmed = String(apiBaseUrl || '').trim().replace(/\/+$/, '');
  if (!trimmed) {
    throw new Error('API base URL is not configured.');
  }
  return trimmed;
};

const parseJson = async <T>(response: Response): Promise<T> => {
  const text = await response.text();
  return text ? JSON.parse(text) as T : ({} as T);
};

export const fetchAutomationBoard = async (apiBaseUrl: string): Promise<AutomationBoardResponse> => {
  const baseUrl = ensureApiBaseUrl(apiBaseUrl);
  const response = await fetch(`${baseUrl}${AUTOMATION_ENDPOINT}`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
  });
  if (!response.ok) {
    const payload = await parseJson<{ error?: string }>(response).catch(() => ({ error: '' }));
    throw new Error(payload.error || `Failed to load automation board (${response.status})`);
  }
  return parseJson<AutomationBoardResponse>(response);
};

export const submitAutomationRequest = async (
  apiBaseUrl: string,
  draft: AutomationRequestDraft,
): Promise<AutomationRequestCreateResponse> => {
  const baseUrl = ensureApiBaseUrl(apiBaseUrl);
  const response = await fetch(`${baseUrl}${AUTOMATION_ENDPOINT}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(draft),
  });
  if (!response.ok) {
    const payload = await parseJson<{ error?: string }>(response).catch(() => ({ error: '' }));
    throw new Error(payload.error || `Failed to submit automation request (${response.status})`);
  }
  return parseJson<AutomationRequestCreateResponse>(response);
};
